import React, { useState, useEffect } from 'react'
import Header from '../../layouts/Header';
import { Button, Col, Form, Row, Table, Spinner, DropdownButton, Dropdown } from 'react-bootstrap';
import { ArrowLeft, FileEarmarkSpreadsheet } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import OutletSelect from '../../components/OutletSelect';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import Select from 'react-select';

const MarketingReport = () => {

    const accessToken = localStorage.getItem("authToken");
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();

    const [reportLoading, setReportLoading] = useState(false);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [clicked, setClicked] = useState(false);

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const [selectedYear, setSelectedYear] = useState({ value: currentYear, label: String(currentYear) });
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedWeekday, setSelectedWeekday] = useState(null);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
            "&:hover": { borderColor: state.isFocused ? "#80bdff" : "#ced4da" },
        }),
        placeholder: (provided) => ({ ...provided, color: "#6c757d" }),
    };

    const yearOptions = Array.from({ length: 3 }, (_, i) => {
        const y = currentYear - i;
        return { value: y, label: String(y) };
    });

    const monthOptions = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' },
    ];

    const weekdayOptions = [
        { value: 0, label: 'Sunday' }, { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' },
    ];

    const handleApiError = (error) => {
        let errorMessage = 'An error occurred';
        if (error.response?.status === 406) {
            errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
        } else if (error.response?.status === 412) {
            errorMessage = error.response?.data?.data?.message;
        } else if (error.response?.status === 500) {
            errorMessage = error.response?.data?.error;
        }
        toast.error(errorMessage, { position: "top-right", autoClose: 3000, theme: "light" });
    };

    const buildQueryParams = (download = false) => {
        const params = new URLSearchParams();
        if (selectedOutlet?.value) params.append('outletId', selectedOutlet.value);
        if (selectedYear?.value) params.append('year', selectedYear.value);
        if (selectedMonth?.value) params.append('month', selectedMonth.value);
        if (selectedWeekday !== null && selectedWeekday?.value !== undefined) params.append('weekday', selectedWeekday.value);
        if (download) params.append('isDownload', true);
        return params.toString();
    };

    const getMarketingReport = async (download = false) => {
        if (download) {
            setDownloadBtnLoading(true);
        } else {
            setReportLoading(true);
            setClicked(true);
        }

        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const queryString = buildQueryParams(download);

            const response = await axiosInstance.get(
                `order/marketingreport?${queryString}`,
                { headers, responseType: download ? 'blob' : 'json' }
            );

            if (download) {
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);

                const outletName = selectedOutlet?.label || 'All Outlets';
                const monthLabel = selectedMonth?.label || 'All Months';
                link.download = `${outletName} Marketing Report (${monthLabel} ${selectedYear?.value}).xlsx`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);

                toast.success('Excel file downloaded successfully!', {
                    position: "top-right", autoClose: 3000, theme: "light"
                });
            } else {
                setReportData(response?.data?.data || []);
            }

        } catch (error) {
            handleApiError(error);
        } finally {
            setReportLoading(false);
            setDownloadBtnLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await getMarketingReport(false);
        toast.success("Data Fetched Successfully", {
            position: "top-right", autoClose: 3000, theme: "light"
        });
    };

    // Helper to format a cell value — blank string " " shows as "—"
    const fmt = (val) => {
        if (val === null || val === undefined || String(val).trim() === '') return '—';
        if (typeof val === 'number') return `₹ ${val.toFixed(2)}`;
        return val;
    };

    // Sum only numeric values in a column
    const colTotal = (key) =>
        reportData.reduce((sum, row) => {
            const v = row[key];
            return typeof v === 'number' ? sum + v : sum;
        }, 0);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0 fw-bold d-inline-block">
                                <i className="bi bi-bar-chart-line me-2"></i>MARKETING REPORT
                            </h4>
                        </div>
                        <div>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => navigate('/reports/all-reports')}
                                className="me-3 d-flex align-items-center fw-semibold"
                                style={{ height: '38px' }}
                            >
                                <ArrowLeft className="me-1" /> Back to Reports
                            </Button>
                        </div>
                    </div>
                    <hr className='mt-0 mb-0' />

                    <div className="card-body">
                        <Form onSubmit={handleSubmit}>
                            {/* Filters */}
                            <div className="p-3 bg-light rounded-3 mb-3">
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group controlId="outletSelect">
                                            <OutletSelect
                                                value={selectedOutlet}
                                                onChange={(option) => setSelectedOutlet(option)}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="yearSelect">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar2 me-2"></i>Year
                                            </Form.Label>
                                            <Select
                                                options={yearOptions}
                                                value={selectedYear}
                                                onChange={(opt) => setSelectedYear(opt)}
                                                styles={customStyles}
                                                placeholder="Select Year"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="monthSelect">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar3 me-2"></i>Month
                                            </Form.Label>
                                            <Select
                                                options={monthOptions}
                                                value={selectedMonth}
                                                onChange={(opt) => setSelectedMonth(opt)}
                                                isClearable
                                                styles={customStyles}
                                                placeholder="Select Month"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="weekdaySelect">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar-week me-2"></i>Weekday
                                            </Form.Label>
                                            <Select
                                                options={weekdayOptions}
                                                value={selectedWeekday}
                                                onChange={(opt) => setSelectedWeekday(opt)}
                                                isClearable
                                                styles={customStyles}
                                                placeholder="Select Weekday"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            {/* Actions */}
                            <div className="mb-4 p-3 bg-light rounded-3">
                                <Row className="g-3 align-items-center">
                                    <Col md={3} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="w-100"
                                            disabled={reportLoading}
                                        >
                                            {reportLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>

                                    <Col md={3}>
                                        <Button
                                            variant="outline-success"
                                            className="fw-semibold w-100 d-flex align-items-center justify-content-center gap-2"
                                            onClick={() => getMarketingReport(true)}
                                            disabled={!reportData.length || downloadBtnLoading}
                                        >
                                            <FileEarmarkSpreadsheet />
                                            {downloadBtnLoading ? <Spinner size="sm" /> : 'Download Report'}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </Form>

                        {/* Results */}
                        {!clicked ? (
                            <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                                <div className="text-center">
                                    <i className="bi bi-bar-chart-line fs-1 text-muted"></i>
                                    <h5 className="text-muted mt-3">
                                        Please select filters and generate report
                                    </h5>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive border rounded-3">
                                <Table striped bordered hover className="mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th width="50">#</th>
                                            <th>Date</th>
                                            <th>Day</th>
                                            <th className="text-end">Total Sales</th>
                                            <th className="text-end">App / Website Sales</th>
                                            <th className="text-end">Kiosk Sales</th>
                                            <th className="text-end">Last Month</th>
                                            <th className="text-end">2nd Last Month</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportLoading ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">
                                                    <Spinner animation="border" variant="primary" />
                                                    <p className="mt-2 mb-0">Loading marketing data...</p>
                                                </td>
                                            </tr>
                                        ) : reportData.length > 0 ? (
                                            <>
                                                {reportData.map((row, index) => (
                                                    <tr key={index}>
                                                        <td className="text-muted">{index + 1}</td>
                                                        <td className="fw-semibold">{row.date}</td>
                                                        <td>{row.day}</td>
                                                        <td className="text-end">{fmt(row.totalSales)}</td>
                                                        <td className="text-end">{fmt(row.appWebsiteSales)}</td>
                                                        <td className="text-end">{fmt(row.kioskSales)}</td>
                                                        <td className="text-end">{fmt(row.lastMonth)}</td>
                                                        <td className="text-end">{fmt(row.secondLastMonth)}</td>
                                                    </tr>
                                                ))}
                                                {/* Totals row */}
                                                <tr className="bg-light fw-bold">
                                                    <td colSpan="3" className="text-end">TOTAL</td>
                                                    <td className="text-end">
                                                        {colTotal('totalSales') ? `₹ ${colTotal('totalSales').toFixed(2)}` : '—'}
                                                    </td>
                                                    <td className="text-end">
                                                        {colTotal('appWebsiteSales') ? `₹ ${colTotal('appWebsiteSales').toFixed(2)}` : '—'}
                                                    </td>
                                                    <td className="text-end">
                                                        {colTotal('kioskSales') ? `₹ ${colTotal('kioskSales').toFixed(2)}` : '—'}
                                                    </td>
                                                    <td className="text-end">
                                                        {colTotal('lastMonth') ? `₹ ${colTotal('lastMonth').toFixed(2)}` : '—'}
                                                    </td>
                                                    <td className="text-end">
                                                        {colTotal('secondLastMonth') ? `₹ ${colTotal('secondLastMonth').toFixed(2)}` : '—'}
                                                    </td>
                                                </tr>
                                            </>
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">
                                                    <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                                                    <p className="mt-2 mb-0 text-muted">
                                                        No data found for the selected criteria
                                                    </p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default MarketingReport;